import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { breakpoints } from 'theme';

export const OutcomePageWrap = styled.div`
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 25px 56px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.pickledBluewood};

  @media ${breakpoints.downSmPlus} {
    padding: 20px 16px 48px;
  }
`;

export const SuccessWrap = styled.div`
  text-align: center;
  padding: 28px 16px 24px;
  max-width: 540px;
  margin: 0 auto;
`;

export const SuccessCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 36px;
`;

export const SuccessTitle = styled.h2`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin: 0 0 8px;
  letter-spacing: -0.02em;
`;

export const SuccessSub = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.65;
  margin: 0 0 28px;
`;

export const SuccessDetails = styled.div`
  background: ${({ theme }) => theme.color.white};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 14px;
  padding: 18px 22px;
  text-align: left;
  margin-bottom: 20px;
`;

export const SuccessDetailsHeading = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.baliHai};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
`;

export const SuccessList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const SuccessListItem = styled.li`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  padding: 4px 0;
  display: flex;
  gap: 9px;
  align-items: flex-start;
  line-height: 1.45;
`;

export const SuccessCheck = styled.span`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
`;

/** Full-width CTA row above Go home / View subscription (onboarding success + Cal embed). */
export const OnboardingScheduleWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
`;

export const SuccessBtns = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

export const BtnPrimaryOutcome = styled.button`
  padding: 13px 32px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #0a1a14;
  }
`;

/** Soft pulse so the Cal booking CTA reads as “next step” without harsh flashing. */
const onboardingScheduleCtaPulse = keyframes`
  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgb(11 60 50 / 35%);
  }
  50% {
    opacity: 0.88;
    box-shadow: 0 0 0 8px rgb(11 60 50 / 0%);
  }
`;

export const BtnOnboardingScheduleCta = styled(BtnPrimaryOutcome)<{
  /** When false, pulse stops (after user opens scheduling). */
  $pulsing?: boolean;
}>`
  ${({ $pulsing = true }) =>
    $pulsing
      ? css`
          animation: ${onboardingScheduleCtaPulse} 1.5s ease-in-out infinite;
        `
      : css`
          animation: none;
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &:hover {
    animation: none;
    opacity: 1;
    box-shadow: none;
  }
`;

export const BtnPrimaryLink = styled(Link)`
  padding: 13px 32px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: background 0.15s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover {
    background: #0a1a14;
    color: ${({ theme }) => theme.color.white};
  }
`;

export const BtnSecondaryOutcome = styled.button`
  padding: 13px 32px;
  border-radius: 40px;
  background: transparent;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.skobeloffGreen};
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

/* Failure */
export const FailureCircle = styled(SuccessCircle)`
  background: #fff0f0;
  font-size: 32px;
`;

export const FailureTitle = styled(SuccessTitle)`
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const FailureSub = styled(SuccessSub)``;
